import type { AppSettings, ChangePasswordInput, UpdateAppSettingsInput } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Descriptions, Form, Input, InputNumber, Tabs, Typography } from 'antd';
import { useEffect } from 'react';
import { apiClient, getApiError } from '../../api/client';
import { changePassword } from '../auth/api';
import { applyServerErrors } from '../../lib/forms';

const { Title, Paragraph, Text } = Typography;

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/** Tab: Cont — task-8d-brief.md §4. Only the password-change form; there is
 * no other account-level setting in v1 (a single admin user, seeded once —
 * see `AdminSeedService`). */
function ContTab() {
  const { message } = App.useApp();
  const [form] = Form.useForm<ChangePasswordFormValues>();

  const mutation = useMutation({
    mutationFn: (input: ChangePasswordInput) => changePassword(input),
    onSuccess: () => {
      message.success('Parola a fost schimbată.');
      form.resetFields();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Parola nu a putut fi schimbată.');
    },
  });

  function handleFinish(values: ChangePasswordFormValues) {
    mutation.mutate({ currentPassword: values.currentPassword, newPassword: values.newPassword });
  }

  return (
    <Card size="small" style={{ maxWidth: 480 }}>
      <Form<ChangePasswordFormValues> form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="currentPassword"
          label="Parola curentă"
          rules={[{ required: true, message: 'Parola curentă este obligatorie.' }]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="Parola nouă"
          rules={[
            { required: true, message: 'Parola nouă este obligatorie.' },
            { min: 10, message: 'Parola nouă trebuie să aibă cel puțin 10 caractere.' },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirmă parola nouă"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Confirmarea parolei este obligatorie.' },
            ({ getFieldValue }) => ({
              validator(_, value: string) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                return Promise.reject(new Error('Parolele nu coincid.'));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            Schimbă parola
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

async function fetchAppSettings(): Promise<AppSettings> {
  const { data } = await apiClient.get<AppSettings>('/settings');
  return data;
}

async function updateAppSettings(input: UpdateAppSettingsInput): Promise<AppSettings> {
  const { data } = await apiClient.put<AppSettings>('/settings', input);
  return data;
}

/** Tab: Fiscalitate — setările globale ale estimărilor fiscale: cota de
 * impozit pe dividende folosită de widgetul Profit din dashboard. Regimul de
 * impozitare (micro/profit) se setează per firmă, în nomenclatorul Firme. */
function FiscalitateTab() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ dividendTaxPercent: string }>();

  const { data: settings } = useQuery({ queryKey: ['app-settings'], queryFn: fetchAppSettings });

  useEffect(() => {
    if (settings) form.setFieldsValue({ dividendTaxPercent: settings.dividendTaxPercent });
  }, [settings, form]);

  const mutation = useMutation({
    mutationFn: updateAppSettings,
    onSuccess: (data) => {
      message.success('Setările au fost salvate.');
      queryClient.setQueryData(['app-settings'], data);
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Setările nu au putut fi salvate.');
    },
  });

  return (
    <Card size="small" style={{ maxWidth: 480 }}>
      <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate({ dividendTaxPercent: String(values.dividendTaxPercent) })}>
        <Form.Item
          name="dividendTaxPercent"
          label="Impozit pe dividende (%)"
          extra="Folosit de widgetul Profit din dashboard la estimarea netului după distribuire. Regimul de impozitare al fiecărei firme se setează în Firme."
          rules={[{ required: true, message: 'Cota este obligatorie.' }]}
        >
          <InputNumber<string> stringMode min="0" max="99.99" precision={2} decimalSeparator="," style={{ width: 160 }} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            Salvează
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

/** Tab: Backup — task-8d-brief.md §4. Purely informative, no API call: the
 * actual backup job runs on the NAS itself, outside this application. */
function BackupTab() {
  return (
    <Card size="small" style={{ maxWidth: 640 }}>
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Locație">
          <Text code>/volume1/backup/financiar</Text> (NAS)
        </Descriptions.Item>
        <Descriptions.Item label="Program">Zilnic, la ora 02:00</Descriptions.Item>
        <Descriptions.Item label="Conținut">Dump complet PostgreSQL (`pg_dump -Fc`) al bazei de date a aplicației.</Descriptions.Item>
      </Descriptions>
      <Paragraph style={{ marginTop: 16, marginBottom: 4 }}>
        <Text strong>Restaurare (rezumat):</Text>
      </Paragraph>
      <Paragraph>
        <Text code>pg_restore --clean --if-exists -d financiar &lt;fișier_backup&gt;</Text>
        <br />
        Rulează comanda pe server, cu baza de date oprită pentru scriere (aplicația oprită), folosind cel mai recent
        fișier din locația de mai sus. Contactează administratorul de sistem înainte de o restaurare — operațiunea
        suprascrie datele curente.
      </Paragraph>
    </Card>
  );
}

/** Tab: Despre — task-8d-brief.md §4. */
function DespreTab() {
  const version = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';
  return (
    <Card size="small" style={{ maxWidth: 480 }}>
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Versiune">{version}</Descriptions.Item>
        <Descriptions.Item label="Stack">NestJS + Prisma + PostgreSQL · React + Ant Design</Descriptions.Item>
        <Descriptions.Item label="e-Factura ANAF">
          <Text type="secondary">Se configurează per firmă, în Firme → editează firma → tab „e-Factura".</Text>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

/** `/setari` (task-8d-brief.md §4). */
export function SetariPage() {
  return (
    <div>
      <Title level={3} style={{ marginBottom: 16 }}>
        Setări
      </Title>
      <Tabs
        items={[
          { key: 'cont', label: 'Cont', children: <ContTab /> },
          { key: 'fiscalitate', label: 'Fiscalitate', children: <FiscalitateTab /> },
          { key: 'backup', label: 'Backup', children: <BackupTab /> },
          { key: 'despre', label: 'Despre', children: <DespreTab /> },
        ]}
      />
    </div>
  );
}
